import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowUp, ArrowDown, Sliders, MagnifyingGlass, ClockCounterClockwise, HandArrowUp } from "@phosphor-icons/react";

const TX = {
  restock: { icon: ArrowUp, label: "Restock", cls: "bg-success/10 text-success" },
  usage: { icon: ArrowDown, label: "Usage", cls: "bg-destructive/10 text-destructive" },
  sale: { icon: ArrowDown, label: "Sale", cls: "bg-destructive/10 text-destructive" },
  issue: { icon: HandArrowUp, label: "Taken out", cls: "bg-destructive/10 text-destructive" },
  adjustment: { icon: Sliders, label: "Adjustment", cls: "bg-warning/10 text-warning" },
};

function fmt(iso) {
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function History() {
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");

  useEffect(() => { api.get("/transactions").then(({ data }) => { setTxs(data); setLoading(false); }); }, []);

  const filtered = useMemo(() => txs.filter((t) => {
    if (type !== "all" && t.type !== type) return false;
    if (q && !`${t.item_name} ${t.note} ${t.reference} ${t.user_name} ${t.taken_by || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [txs, q, type]);

  return (
    <div>
      <PageHeader title="History" subtitle="Full log of every stock movement" />
      <div className="p-6 md:p-8 space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="history-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search item, note, reference…" className="pl-9" />
          </div>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-44" data-testid="history-filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="restock">Restock</SelectItem>
              <SelectItem value="usage">Usage</SelectItem>
              <SelectItem value="sale">Sale</SelectItem>
              <SelectItem value="issue">Taken out</SelectItem>
              <SelectItem value="adjustment">Adjustment</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="h-40 flex items-center justify-center text-muted-foreground text-sm">Loading history…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-card border border-border rounded-md p-12 text-center text-muted-foreground" data-testid="history-empty">
            <ClockCounterClockwise size={28} className="mx-auto mb-2 opacity-40" />
            No stock movements recorded yet.
          </div>
        ) : (
          <div className="bg-card border border-border rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="history-table">
                <thead>
                  <tr className="border-b border-border bg-accent/60 text-left">
                    <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground">Type</th>
                    <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground">Item</th>
                    <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground text-right">Change</th>
                    <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground text-right">After</th>
                    <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground">Note</th>
                    <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground">By</th>
                    <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground text-right">When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((t) => {
                    const meta = TX[t.type] || TX.adjustment;
                    const debit = t.quantity_change < 0;
                    return (
                      <tr key={t.id} data-testid={`history-row-${t.id}`} className="hover:bg-accent/40 transition-colors duration-150">
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-xs font-medium ${meta.cls}`}>
                            <meta.icon size={13} weight="bold" /> {meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-medium">{t.item_name}</td>
                        <td className={`px-4 py-3 text-right tnums font-medium ${debit ? "text-destructive" : "text-success"}`}>{debit ? "" : "+"}{t.quantity_change}</td>
                        <td className="px-4 py-3 text-right tnums text-muted-foreground">{t.quantity_after} {t.unit || ""}</td>
                        <td className="px-4 py-3 text-muted-foreground max-w-[220px] truncate">{t.taken_by ? <span className="text-foreground font-medium">{t.taken_by} · </span> : null}{t.reference ? `[${t.reference}] ` : ""}{t.note || (t.taken_by ? "" : "—")}</td>
                        <td className="px-4 py-3 text-muted-foreground">{t.user_name}</td>
                        <td className="px-4 py-3 text-right text-muted-foreground whitespace-nowrap">{fmt(t.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
